import React, { useEffect, useState, useRef, Suspense, lazy, startTransition } from 'react';
import { CssBaseline } from '@mui/material';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { SnackbarProvider } from 'notistack';
import { 
  Box, 
  CircularProgress, 
  Typography,
  Backdrop,
  LinearProgress,
  Paper,
  useTheme
} from '@mui/material';
import { DNA, Vortex } from 'react-loader-spinner';
import ProtectedRoute from './routes/ProtectedRoute';
import ErrorBoundary from './components/common/ErrorBoundary';

// Theme - Keep these as they're needed immediately
import { ThemeProvider } from './contexts/ThemeContext';
import ThemeSelector from './components/common/ThemeSelector';
import SplashScreen from './components/common/SplashScreen';

// Layout - Keep these as they're needed immediately
import MobileLayout from './components/admin/MobileLayout';
import ComingSoon from './components/admin/ComingSoon';
import BDAMobileLayout from './components/bda/BDAMobileLayout';
import BDALayout from './layouts/BDALayout';
import ServiceProviderLayout from './layouts/ServiceProviderLayout';
import SPStaffLayout from './layouts/SPStaffLayout';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { FilterProvider } from './contexts/FilterContext';
import { DoctorViewProvider } from './contexts/DoctorViewContext';
import NativeService from './services/NativeService';
import useStatusBar from './hooks/useStatusBar';
import { FaHandHoldingMedical } from "react-icons/fa";

// Lazy load all page components
const DoctorSchedule = lazy(() => import('./components/doctors/DoctorSchedule'));
const SPRegister = lazy(() => import('./SPRegister'));


// Admin Pages - Lazy loaded
const Profile = lazy(() => import('./pages/admin/Profile'));
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const Users = lazy(() => import('./pages/admin/Users'));
const EasyOP = lazy(() => import('./pages/admin/EasyOP'));
const Notifications = lazy(() => import('./pages/admin/Notifications'));
const Settings = lazy(() => import('./pages/admin/Settings'));
const Login = lazy(() => import('./components/common/Login'));
const AdminUserList = lazy(() => import('./pages/admin/administrators/AdminUserList'));
const AdminUserForm = lazy(() => import('./pages/admin/administrators/AdminUserForm'));
const BDAList = lazy(() => import('./pages/admin/bda/BDAList'));
const ServiceProviderList = lazy(() => import('./pages/admin/serviceproviders/ServiceProviderList'));
const EasyOPUserList = lazy(() => import('./pages/admin/easyopusers/EasyOPUserList'));
const EasyOPUserForm = lazy(() => import('./pages/admin/easyopusers/EasyOPUserForm'));
const BookMyOPUserList = lazy(() => import('./pages/admin/bookmyopusers/BookMyOPUserList'));
const BookMyOPUserForm = lazy(() => import('./pages/admin/bookmyopusers/BookMyOPUserForm'));
const AdminUserDetail = lazy(() => import('./pages/admin/administrators/AdminUserDetail'));
const BDAForm = lazy(() => import('./pages/admin/bda/BDAForm'));
const BDADetail = lazy(() => import('./pages/admin/bda/BDADetail'));
const ServiceProviderEnrollment = lazy(() => import('./pages/admin/serviceproviders/ServiceProviderEnrollment'));
const ServiceProviderDetail = lazy(() => import('./pages/admin/serviceproviders/ServiceProviderDetail'));
const ServiceProviderForm = lazy(() => import('./pages/admin/serviceproviders/ServiceProviderForm'));
const Plugs = lazy(() => import('./pages/admin/Plugs'));
const ServiceProviderTariffs = lazy(() => import('./pages/admin/plugs/tariffs/ServiceProviderTariffs'));
const CardTariffs = lazy(() => import('./pages/admin/plugs/tariffs/CardTariffs'));
const MembershipPromotions = lazy(() => import('./pages/admin/plugs/membership/MembershipPromotions'));
const AdvertisementControl = lazy(() => import('./pages/admin/plugs/advertisements/AdvertisementControl'));
const ServiceProviderTypes = lazy(() => import('./pages/admin/plugs/service-provider-types/ServiceProviderTypes'));
const BDACommissionManagement = lazy(() => import('./pages/admin/plugs/bda-commission/BDACommissionManagement'));
const SpecialtyManagement = lazy(() => import('./pages/admin/plugs/specialties/SpecialtyManagement'));
const CardTariffManagement = lazy(() => import('./pages/admin/plugs/card-tariffs/CardTariffManagement'));
// ... existing imports ...
const Qualifications = lazy(() => import('./pages/admin/plugs/qualifications/Qualifications'));
const Organizations = lazy(() => import('./pages/admin/plugs/organizations/organizations'));

// ... existing code ...
// Home Pages - Lazy loaded
const BDARegister = lazy(() => import('./pages/Home/BDARegister'));
const TrackBDAStatus = lazy(() => import('./components/TrackBDAStatus'));
const BDASettings = lazy(() => import('./pages/bda/BDASettings'));
const HomePage = lazy(() => import('./pages/Home/HomePage'));
const AddSchedule = lazy(() => import('./pages/serviceprovider/AddSchedule'));
const Careers = lazy(() => import('./pages/Home/Careers'));
const AboutUs = lazy(() => import('./pages/Home/AboutUs'));
const Contact = lazy(() => import('./pages/Home/Contact'));
const ServiceProviders = lazy(() => import('./pages/Home/ServiceProviders'));
const PrivacyPolicy = lazy(() => import('./pages/Home/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/Home/TermsOfService'));
const DeleteAccount = lazy(() => import('./pages/Home/DeleteAccount'));

// Tickets - Lazy loaded
const Tickets = lazy(() => import('./pages/admin/tickets/index'));
const TicketDetail = lazy(() => import('./pages/admin/tickets/[id]'));

// BDA Pages - Lazy loaded
const BDADashboard = lazy(() => import('./pages/bda/Dashboard'));
const BDASPList = lazy(() => import('./pages/bda/sp/BDASPList'));
const BDAServiceProviderForm = lazy(() => import('./pages/bda/ServiceProviderForm'));
const BDAServiceProviderDetails = lazy(() => import('./pages/bda/sp/BDASPTabs'));
const BDAPatients = lazy(() => import('./pages/bda/Patients'));
const BDAProfile = lazy(() => import('./pages/bda/Profile'));
const BDATickets = lazy(() => import('./pages/bda/tickets/index'));
const BDATicketDetail = lazy(() => import('./pages/bda/tickets/[id]'));
const BDANotifications = lazy(() => import('./pages/bda/Notifications'));
const EasyOpCards = lazy(() => import('./pages/bda/EasyOpCardsDrawer'));

// Service Provider Pages - Lazy loaded
const SPDashboard = lazy(() => import('./pages/serviceprovider/Dashboard'));
const SPSchedule = lazy(() => import('./pages/serviceprovider/Schedule'));
const SPDoctors = lazy(() => import('./pages/serviceprovider/Doctors/Doctors'));
const SPPatients = lazy(() => import('./pages/serviceprovider/Patients'));
const SPProfile = lazy(() => import('./pages/serviceprovider/SPProfile'));
const SPTickets = lazy(() => import('./pages/serviceprovider/tickets/index'));
const SPTicketDetail = lazy(() => import('./pages/serviceprovider/tickets/[id]'));
const SPNotifications = lazy(() => import('./pages/serviceprovider/Notifications'));
const SPAppointments = lazy(() => import('./pages/serviceprovider/Appointments'));
const SPAppointmentsList = lazy(() => import('./pages/serviceprovider/AppointmentsList'));
const SPSettings = lazy(() => import('./pages/serviceprovider/Settings'));
const SPMembership = lazy(() => import('./pages/serviceprovider/membership'));
const FirstLoginPasswordChange = lazy(() => import('./pages/serviceprovider/FirstLoginPasswordChange'));
const Doctors = lazy(() => import('./pages/serviceprovider/Doctors/Doctors'));
const DoctorForm = lazy(() => import('./pages/serviceprovider/DoctorForm'));
const DoctorDetail = lazy(() => import('./pages/serviceprovider/Doctors/DoctorDetail'));

// Additional components that need lazy loading
const StaffList = lazy(() => import('./pages/serviceprovider/StaffList'));
const StaffDashboard = lazy(() => import('./pages/serviceprovider/staff/StaffDashboard'));
const StaffSchedule = lazy(() => import('./pages/serviceprovider/staff/StaffSchedule'));
// StaffPatients component doesn't exist, removing this import
const StaffProfile = lazy(() => import('./pages/serviceprovider/staff/StaffProfile'));
const StaffTickets = lazy(() => import('./pages/serviceprovider/staff/StaffTickets'));
const StaffNotifications = lazy(() => import('./pages/serviceprovider/staff/StaffNotifications'));
const WalkInPatients = lazy(() => import('./pages/admin/walkinpatients/WalkInPatients'));
const WalkInAppointmentDetails = lazy(() => import('./pages/admin/walkinpatients/WalkInAppointmentDetails'));
const AdvertisementCosts = lazy(() => import('./pages/admin/plugs/advertisements/AdvertisementCosts'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Additional lazy imports for remaining components
const SPTabs = lazy(() => import('./pages/admin/sp/SPTabs'));
const SPList = lazy(() => import('./pages/admin/sp/SPList'));
// const BDASPList = lazy(() => import('./pages/bda/sp/BDASPList'));
const BDASPTabs = lazy(() => import('./pages/bda/sp/BDASPTabs'));
const BookMyOpUsers = lazy(() => import('./pages/admin/bookmyop/BookMyOpUsers'));
const BookMyOpPatientDetails = lazy(() => import('./pages/admin/bookmyop/BookMyOpPatientDetails'));
const EasyOpDetail = lazy(() => import('./pages/admin/easyopusers/EasyOpDetail'));
const BDACards = lazy(() => import('./pages/bda/BDACards'));
const AppointmentsList = lazy(() => import('./pages/admin/appointments/AppointmentsList'));
const AppointmentDetails = lazy(() => import('./pages/admin/appointments/AppointmentDetails'));
const SPAdvertisements = lazy(() => import('./pages/serviceprovider/SPAdvertisements'));
const StaffAppointments = lazy(() => import('./pages/serviceprovider/staff/StaffAppointments'));


// Context providers - Keep these as they're needed immediately

// Loading component for lazy-loaded routes
const LoadingFallback = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="200px"
    flexDirection="column"
    gap={2}
  >
    <CircularProgress size={40} />
    <Typography variant="body2" color="text.secondary">
      Loading...
    </Typography>
  </Box>
);

// Helper function to wrap components with Suspense
const withSuspense = (Component) => (
  <Suspense fallback={<LoadingFallback />}>
    {Component}
  </Suspense>
);

const AppContent = () => {
  const { isAuthenticated, userRole, user, loading, appActive } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const intervalRef = useRef(null);
  const theme = useTheme();
  
  // Initialize status bar with theme
  useStatusBar();
  
  useEffect(() => {
    // Hide splash screen after app is loaded
    NativeService.hideSplashScreen();
    
    // Simulate loading progression
    intervalRef.current = setInterval(() => {
      setLoadingProgress(prev => {
        const newProgress = prev + 15;
        if (newProgress >= 100) {
          clearInterval(intervalRef.current);
          // Give a small delay after reaching 100% before hiding splash screen
          setTimeout(() => {
            setIsLoading(false);
          }, 500);
          return 100;
        }
        return newProgress;
      });
    }, 500);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []); // Empty dependency array to run once on mount
  
  // Log app state changes for debugging
  useEffect(() => {
    console.log('App state changed:', { appActive, isAuthenticated, userRole });
  }, [appActive, isAuthenticated, userRole]);
  
  if (isLoading || loading) {
    return <SplashScreen progress={loadingProgress} />;
  }
  
  // Get userType for correct redirect
  const userType = user?.user_type || userRole?.replace('-', '_');
  
  // Determine default route based on user type
  const getDefaultRoute = () => {
    const userRole = localStorage.getItem('userRole');
    const userType = user?.user_type;
    
    console.log('getDefaultRoute - Debug:', {
      userRole,
      userType,
      user: user ? { id: user.id, email: user.email, user_type: user.user_type } : null
    });
    
    if (!userRole) return "/login";
    
    if (userRole === 'bda') return "/bda/dashboard";
    if (userRole === 'service-provider') return "/sp/dashboard";
    if (userRole === 'sp_staff' || userType === 'sp_staff') {
      console.log('Redirecting to staff dashboard');
      return "/sp/staff/dashboard";
    }
    if (userRole === 'sp_admin' || userType === 'sp_admin') return "/sp/dashboard";
    if (userRole === 'admin') return "/dashboard";
    
    console.log('No matching role, returning login');
    return "/login"; // Fallback for unknown roles
  };

  // Navigation handler with startTransition
  const handleNavigation = (to) => {
    startTransition(() => {
      // Navigation logic will be handled by React Router
    });
  };
  
  return (
    <Routes >
      <Route path="/" element={
        isAuthenticated ? <Navigate to={getDefaultRoute()} replace /> : (
          <Suspense fallback={<LoadingFallback />}>
            <HomePage />
          </Suspense>
        )
      } />
      <Route path="/doctor-schedule" element={
        <Suspense fallback={<LoadingFallback />}>
          <DoctorSchedule />
        </Suspense>
      } />
      <Route path="/careers" element={
        <Suspense fallback={<LoadingFallback />}>
          <Careers />
        </Suspense>
      } />
      <Route path="/about-us" element={
        <Suspense fallback={<LoadingFallback />}>
          <AboutUs />
        </Suspense>
      } />
      <Route path="/contact" element={
        <Suspense fallback={<LoadingFallback />}>
          <Contact />
        </Suspense>
      } />
      <Route path="/service-providers" element={
        <Suspense fallback={<LoadingFallback />}>
          <ServiceProviders />
        </Suspense>
      } />
      <Route path="/privacy-policy" element={
        <Suspense fallback={<LoadingFallback />}>
          <PrivacyPolicy />
        </Suspense>
      } />
      <Route path="/terms-of-service" element={
        <Suspense fallback={<LoadingFallback />}>
          <TermsOfService />
        </Suspense>
      } />
      <Route path="/delete-account" element={
        <Suspense fallback={<LoadingFallback />}>
          <DeleteAccount />
        </Suspense>
      } />
      
      <Route path="/register" element={
        <Suspense fallback={<LoadingFallback />}>
          <SPRegister />
        </Suspense>
      } />
      <Route path="/login" element={
        localStorage.getItem('userRole') ? <Navigate to={getDefaultRoute()} replace /> : (
          <Suspense fallback={<LoadingFallback />}>
            <Login />
          </Suspense>
        )
      } />
      
      <Route path="/admin/login" element={<Navigate to="/login" replace />} />
      <Route path="/bda/login" element={<Navigate to="/login" replace />} />
      <Route path="/bda/register" element={
        <Suspense fallback={<LoadingFallback />}>
          <BDARegister />
        </Suspense>
      } />
      
      <Route path="/bda/track-status" element={
        <ProtectedRoute roles={['bda']} path="/bda/track-status">
          <Suspense fallback={<LoadingFallback />}>
            <TrackBDAStatus />
          </Suspense>
        </ProtectedRoute>
      } />
      
      <Route path="/bda/dashboard" element={
        <ProtectedRoute 
          component={(props) => <BDALayout title="BDA Dashboard" {...props} />} 
          roles={['bda']} 
          path="/bda/dashboard"
        >
          <Suspense fallback={<LoadingFallback />}>
            <BDADashboard />
          </Suspense>
        </ProtectedRoute>
      } />
      
      <Route path="/bda/service-providers" element={
        <ProtectedRoute 
          component={(props) => <BDALayout title="Service Providers" {...props} />} 
          roles={['bda']} 
          path="/bda/service-providers"
        >
          <Suspense fallback={<LoadingFallback />}>
            <BDASPList />
          </Suspense>
        </ProtectedRoute>
      } />

      <Route path="/bda/service-providers/add" element={
        <ProtectedRoute 
          component={(props) => <BDALayout title="Register Provider" {...props} />} 
          roles={['bda']} 
          path="/bda/service-providers/add"
        >
          <Suspense fallback={<LoadingFallback />}>
            <BDAServiceProviderForm />
          </Suspense>
        </ProtectedRoute>
      } />
      
      <Route path="/bda/service-providers/:id" element={
        <ProtectedRoute 
          component={(props) => <BDALayout title="Provider Details" {...props} />} 
          roles={['bda']} 
          path="/bda/service-providers/:id"
        >
          <Suspense fallback={<LoadingFallback />}>
            <BDAServiceProviderDetails />
          </Suspense>
        </ProtectedRoute>
      } />
      
      <Route path="/bda/service-providers/:id/edit" element={
        <ProtectedRoute 
          component={(props) => <BDALayout title="Edit Provider" {...props} />} 
          roles={['bda']} 
          path="/bda/service-providers/:id/edit"
        >
          <Suspense fallback={<LoadingFallback />}>
            <BDAServiceProviderForm />
          </Suspense>
        </ProtectedRoute>
      } />
      
      <Route path="/bda/patients" element={
        <ProtectedRoute 
          component={(props) => <BDALayout title="Patients" {...props} />} 
          roles={['bda']} 
          path="/bda/patients"
        >
          <Suspense fallback={<LoadingFallback />}>
            <BDAPatients />
          </Suspense>
        </ProtectedRoute>
      } />
      
      <Route path="/bda/tickets" element={
        <ProtectedRoute 
          component={(props) => <BDALayout title="Support Tickets" {...props} />} 
          roles={['bda']} 
          path="/bda/tickets"
        >
          <Suspense fallback={<LoadingFallback />}>
            <BDATickets />
          </Suspense>
        </ProtectedRoute>
      } />
      
      <Route path="/bda/tickets/:id" element={
        <ProtectedRoute 
          component={(props) => <BDALayout title="Ticket Details" {...props} />} 
          roles={['bda']} 
          path="/bda/tickets/:id"
        >
          <Suspense fallback={<LoadingFallback />}>
            <BDATicketDetail />
          </Suspense>
        </ProtectedRoute>
      } />
      
      <Route path="/bda/notifications" element={
        <ProtectedRoute roles={['bda']}>
          <BDALayout>
            <Suspense fallback={<LoadingFallback />}>
              <BDANotifications />
            </Suspense>
          </BDALayout>
        </ProtectedRoute>
      } />
      
      <Route path="/bda/easyop-patients" element={
        <ProtectedRoute roles={['bda']}>
          <BDALayout>
            <Suspense fallback={<LoadingFallback />}>
              <BDACards />
            </Suspense>
          </BDALayout>
        </ProtectedRoute>
      } />
      
      <Route path="/bda/clients" element={
        <ProtectedRoute roles={['bda']}>
          <BDALayout>
            <Suspense fallback={<LoadingFallback />}>
              <EasyOpCards />
            </Suspense>
          </BDALayout>
        </ProtectedRoute>
      } />
      
      <Route path="/bda/profile" element={
        <ProtectedRoute 
          component={(props) => <BDALayout title="My Profile" {...props} />} 
          roles={['bda']} 
          path="/bda/profile"
        >
          <Suspense fallback={<LoadingFallback />}>
            <BDAProfile />
          </Suspense>
        </ProtectedRoute>
      } />

      <Route path="/bda/settings" element={
        <ProtectedRoute 
          component={(props) => <BDALayout title="Settings" {...props} />} 
          roles={['bda']} 
          path="/bda/settings"
        >
          <Suspense fallback={<LoadingFallback />}>
            <BDASettings />
          </Suspense>
        </ProtectedRoute>
      } />
      
      <Route path="/sp/login" element={<Navigate to="/login" replace />} />

      <Route path="/sp/password-change" element={
        <FirstLoginPasswordChange />
      } />

      <Route path="/sp/dashboard" element={
        <ProtectedRoute 
          component={(props) => <ServiceProviderLayout title="Dashboard" {...props} />} 
          roles={['sp_admin']} 
          path="/sp/dashboard"
        >
          <SPDashboard />
        </ProtectedRoute>
      } />
     
      <Route path="/sp/staff-management" element={
        <ProtectedRoute 
          component={(props) => <ServiceProviderLayout title="Staff Management" {...props} />} 
          roles={['sp_admin']} 
          path="/sp/staff-management"
        >
          <StaffList />
        </ProtectedRoute>
      } />

      <Route path="/sp/schedule" element={
        <ProtectedRoute 
          component={(props) => <ServiceProviderLayout title="Schedule" {...props} />} 
          roles={['sp_admin']} 
          path="/sp/schedule"
        >
          <SPSchedule />
        </ProtectedRoute>
      } />

      <Route path="/sp/schedule/new" element={
        <ProtectedRoute 
          component={(props) => <ServiceProviderLayout title="New Appointment" {...props} />} 
          roles={['sp_admin', 'sp_staff']} 
          path="/sp/schedule/new"
        >
          <SPSchedule />
        </ProtectedRoute>
      } />

      <Route path="/sp/schedule/:id" element={
        <ProtectedRoute 
          component={(props) => <ServiceProviderLayout title="Appointment Details" {...props} />} 
          roles={['sp_admin', 'sp_staff']} 
          path="/sp/schedule/:id"
        >
          <SPSchedule />
        </ProtectedRoute>
      } />

      <Route path="/sp/schedule/add" element={
        <ProtectedRoute 
          component={(props) => <ServiceProviderLayout title="Add Schedule" {...props} />} 
          roles={['sp_admin', 'sp_staff']} 
          path="/sp/schedule/add"
        >
          <AddSchedule />
        </ProtectedRoute>
      } />

      <Route path="/sp/appointments" element={
        <ProtectedRoute 
          component={(props) => <ServiceProviderLayout title="Appointments" {...props} />} 
          roles={['sp_admin']} 
          path="/sp/appointments"
        >
          <SPAppointments />
        </ProtectedRoute>
      } />

      <Route path="/sp/allappointments" element={
        <ProtectedRoute 
          component={(props) => <ServiceProviderLayout title="All Appointments" {...props} />} 
          roles={['sp_admin', 'sp_staff']} 
          path="/sp/allappointments"
        >
          <SPAppointmentsList />
        </ProtectedRoute>
      } />

   

      <Route path="/sp/doctors/new" element={
        <ProtectedRoute 
          component={(props) => <ServiceProviderLayout title="Add Doctor" {...props} />} 
          roles={['sp_admin', 'sp_staff']} 
          path="/sp/doctors/new"
        >
          <DoctorForm />
        </ProtectedRoute>
      } />

      <Route path="/sp/doctors/:id/edit" element={
        <ProtectedRoute 
          component={(props) => <ServiceProviderLayout title="Edit Doctor" {...props} />} 
          roles={['sp_admin', 'sp_staff']} 
          path="/sp/doctors/:id/edit"
        >
          <DoctorForm />
        </ProtectedRoute>
      } />

      <Route path="/sp/doctors" element={
        <ProtectedRoute 
          component={(props) => <ServiceProviderLayout title="Doctors" {...props} />} 
          roles={['sp_admin', 'sp_staff']} 
          path="/sp/doctors"
        >
          <Doctors />
        </ProtectedRoute>
      } />
      <Route path="/sp/doctors/:doctorId" element={
        <ProtectedRoute 
          component={(props) => <ServiceProviderLayout title="Doctor Details" {...props} />} 
          roles={['sp_admin', 'sp_staff']} 
          path="/sp/doctors/:doctorId"
        >
          <DoctorDetail />
        </ProtectedRoute>
      } />

      <Route path="/sp/patients" element={
        <ProtectedRoute 
          component={(props) => <ServiceProviderLayout title="Patients" {...props} />} 
          roles={['sp_admin', 'sp_staff']} 
          path="/sp/patients"
        >
          <SPPatients />
        </ProtectedRoute>
      } />

      <Route path="/sp/profile" element={
        <ProtectedRoute 
          component={(props) => <ServiceProviderLayout title="My Profile" {...props} />} 
          roles={['sp_admin']} 
          path="/sp/profile"
        >
          <SPProfile />
        </ProtectedRoute>
      } />
      <Route path="/sp/advertisements" element={
        <ProtectedRoute 
          component={(props) => <ServiceProviderLayout title="Advertisements" {...props} />} 
          roles={['sp_admin']} 
          path="/sp/advertisements"
        >
          <SPAdvertisements />
        </ProtectedRoute>
      } />

      <Route path="/sp/memberships" element={
        <ProtectedRoute 
          component={(props) => <ServiceProviderLayout title="Membership" {...props} />} 
          roles={['sp_admin']} 
          path="/sp/memberships"
        >
          <SPMembership />
        </ProtectedRoute>
      } />

      <Route path="/sp/notifications" element={
        <ProtectedRoute 
          component={(props) => <ServiceProviderLayout title="Notifications" {...props} />} 
          roles={['sp_admin']} 
          path="/sp/notifications"
        >
          <SPNotifications />
        </ProtectedRoute>
      } />
      
      <Route path="/sp/settings" element={
        <ProtectedRoute 
          component={(props) => <ServiceProviderLayout title="Settings" {...props} />} 
          roles={['sp_admin', 'sp_staff']} 
          path="/sp/settings"
        >
          <SPSettings />
        </ProtectedRoute>
      } />
      
      <Route path="/sp/tickets" element={
        <ProtectedRoute 
          component={(props) => <ServiceProviderLayout title="Support Tickets" {...props} />} 
          roles={['sp_admin']} 
          path="/sp/tickets"
        >
          <SPTickets />
        </ProtectedRoute>
      } />
      
      <Route path="/sp/tickets/:id" element={
        <ProtectedRoute 
          component={(props) => <ServiceProviderLayout title="Ticket Details" {...props} />} 
          roles={['sp_admin', 'sp_staff']} 
          path="/sp/tickets/:id"
        >
          <SPTicketDetail />
        </ProtectedRoute>
      } />


      {/* Staff-specific routes */}
      <Route path="/sp/staff/dashboard" element={
        <ProtectedRoute 
          component={(props) => <SPStaffLayout title="Doctor Dashboard" {...props} />} 
          roles={['sp_admin', 'sp_staff']} 
          path="/sp/staff/dashboard"
        >
          <StaffDashboard />
        </ProtectedRoute>
      } />

      <Route path="/sp/staff/appointments" element={
        <ProtectedRoute 
          component={(props) => <SPStaffLayout title="My Appointments" {...props} />} 
          roles={['sp_admin', 'sp_staff']} 
          path="/sp/staff/appointments"
        >
          <StaffAppointments />
        </ProtectedRoute>
      } />

      <Route path="/sp/staff/schedule" element={
        <ProtectedRoute 
          component={(props) => <SPStaffLayout title="My Schedule" {...props} />} 
          roles={['sp_admin', 'sp_staff']} 
          path="/sp/staff/schedule"
        >
          <StaffSchedule />
        </ProtectedRoute>
      } />

      <Route path="/sp/staff/profile" element={
        <ProtectedRoute 
          component={(props) => <SPStaffLayout title="My Profile" {...props} />} 
          roles={['sp_admin', 'sp_staff']} 
          path="/sp/staff/profile"
        >
          <StaffProfile />
        </ProtectedRoute>
      } />

      <Route path="/sp/staff/tickets" element={
        <ProtectedRoute 
          component={(props) => <SPStaffLayout title="Support Tickets" {...props} />} 
          roles={['sp_admin', 'sp_staff']} 
          path="/sp/staff/tickets"
        >
          <StaffTickets />
        </ProtectedRoute>
      } />

      <Route path="/sp/staff/notifications" element={
        <ProtectedRoute 
          component={(props) => <SPStaffLayout title="Notifications" {...props} />} 
          roles={['sp_admin', 'sp_staff']} 
          path="/sp/staff/notifications"
        >
          <StaffNotifications />
        </ProtectedRoute>
      } />

      <Route path="/users" element={
        <ProtectedRoute 
          component={(props) => <MobileLayout title="User Management" {...props} />} 
          roles={['super_admin', 'admin_staff', 'admin']} 
          path="/users"
        >
          <Suspense fallback={<LoadingFallback />}>
            <Users />
          </Suspense>
        </ProtectedRoute>
      } />

      <Route path="/users/administrators" element={
        <ProtectedRoute 
          component={(props) => <MobileLayout title="Administrators" {...props} />} 
          roles={['super_admin', 'admin_staff', 'admin']} 
          path="/users/administrators"
        >
          <Suspense fallback={<LoadingFallback />}>
            <AdminUserList />
          </Suspense>
        </ProtectedRoute>
      } />
      
      <Route path="/users/administrators/new" element={
        <ProtectedRoute 
          component={(props) => <MobileLayout title="Add Administrator" {...props} />} 
          roles={['super_admin', 'admin_staff']} 
          path="/users/administrators/new"
        >
          <Suspense fallback={<LoadingFallback />}>
            <AdminUserForm />
          </Suspense>
        </ProtectedRoute>
      } />
      
      <Route path="/users/administrators/:id" element={
        <ProtectedRoute 
          component={(props) => <MobileLayout title="Administrator Details" {...props} />} 
          roles={['super_admin', 'admin_staff']} 
          path="/users/administrators/:id"
        >
          <AdminUserDetail />
        </ProtectedRoute>
      } />
      
      <Route path="/users/administrators/:id/edit" element={
        <ProtectedRoute 
          component={(props) => <MobileLayout title="Edit Administrator" {...props} />} 
          roles={['super_admin', 'admin_staff']} 
          path="/users/administrators/:id/edit"
        >
          <AdminUserForm />
        </ProtectedRoute>
      } />
      
      <Route path="/users/bda" element={
        <ProtectedRoute 
          component={(props) => <MobileLayout title="BDA Management" {...props} />} 
          roles={['super_admin', 'admin_staff']} 
          path="/users/bda"
        >
          <BDAList />
        </ProtectedRoute>
      } />
      
      <Route path="/users/service-providers" element={
        <ProtectedRoute 
          component={(props) => <MobileLayout title="Service Providers" {...props} />} 
          roles={['super_admin', 'admin_staff']} 
          path="/users/service-providers"
        >
          <SPList />
        </ProtectedRoute>
      } />
      
      <Route path="/users/service-providers/enroll" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/users/service-providers/enroll">
          <ServiceProviderEnrollment />
        </ProtectedRoute>
      } />
      
      <Route path="/users/service-providers/:id" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/users/service-providers/:id">
          <ServiceProviderDetail />
        </ProtectedRoute>
      } />
      
      <Route path="/users/service-providers/:id/edit" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/users/service-providers/:id/edit">
          <ServiceProviderForm />
        </ProtectedRoute>
      } />

      <Route path="/users/service-providers/:id/tabs" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/users/service-providers/:id/tabs">
          <SPTabs />
        </ProtectedRoute>
      } />
      
      <Route path="/users/easyop-users" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/users/easyop-users">
          <EasyOPUserList />
        </ProtectedRoute>
      } />
      
      <Route path="/users/easyop-users/new" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/users/easyop-users/new">
          <EasyOPUserForm />
        </ProtectedRoute>
      } />
      
      <Route path="/users/easyop-users/:id/edit" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/users/easyop-users/:id/edit">
          <EasyOPUserForm />
        </ProtectedRoute>
      } />
      
      <Route path="/users/easyop-users/:id" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/users/easyop-users/:id">
          <EasyOpDetail />
        </ProtectedRoute>
      } />
      
      <Route path="/users/bookmyop-users" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/users/bookmyop-users">
          <BookMyOPUserList />
        </ProtectedRoute>
      } />
      
      <Route path="/users/bookmyop-users/new" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/users/bookmyop-users/new">
          <BookMyOPUserForm />
        </ProtectedRoute>
      } />
      
      <Route path="/users/bookmyop-users/:id/edit" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/users/bookmyop-users/:id/edit">
          <BookMyOPUserForm />
        </ProtectedRoute>
      } />
      
      <Route path="/users/bookmyop-users/:id" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/users/bookmyop-users/:id">
          <BookMyOPUserForm />
        </ProtectedRoute>
      } />
      
      <Route path="/plugs" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/plugs">
          <Plugs />
        </ProtectedRoute>
      } />
      
      <Route path="/plugs/tariffs/service-providers" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/plugs/tariffs/service-providers">
          <ServiceProviderTariffs />
        </ProtectedRoute>
      } />
      
      <Route path="/plugs/tariffs/cards" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/plugs/tariffs/cards">
          <CardTariffs />
        </ProtectedRoute>
      } />
      
      <Route path="/plugs/membership/promotions" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/plugs/membership/benefits">
          <MembershipPromotions />
        </ProtectedRoute>
      } />
      
      <Route path="/plugs/membership/promo-codes" element={<Navigate to="/plugs/membership/benefits" replace />} />
      
      <Route path="/plugs/advertisements" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/plugs/advertisements/posters">
          <AdvertisementControl />
        </ProtectedRoute>
      } />
      
      <Route path="/plugs/advertisements/system" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/plugs/advertisements/system">
          <AdvertisementControl />
        </ProtectedRoute>
      } />
      
      <Route path="/plugs/service-provider-types" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/plugs/service-provider-types">
          <ServiceProviderTypes />
        </ProtectedRoute>
      } />
      
      <Route path="/plugs/bda-commission" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/plugs/bda-commission">
          <BDACommissionManagement />
        </ProtectedRoute>
      } />
      
      <Route path="/plugs/specialties" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/plugs/specialties">
          <SpecialtyManagement />
        </ProtectedRoute>
      } />
      
      <Route path="/plugs/card-tariffs" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/plugs/card-tariffs">
          <CardTariffManagement />
        </ProtectedRoute>
      } />
      
      // ... existing routes ...
      <Route path="/plugs/qualifications" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/plugs/qualifications">
          <Qualifications />
        </ProtectedRoute>
      } />
      
      <Route path="/plugs/organizations" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/plugs/organizations">
          <Organizations />
        </ProtectedRoute>
      } />
      
    
      
      <Route path="/notifications" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/notifications">
          <Notifications />
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/settings">
          <Settings />
        </ProtectedRoute>
      } />
      
      <Route path="/profile" element={
        <ProtectedRoute 
          component={(props) => <MobileLayout title="My Profile" {...props} />} 
          roles={['super_admin', 'admin_staff', 'admin']} 
          path="/profile"
        >
          <Profile />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/appointments" element={
        <ProtectedRoute 
          component={(props) => <MobileLayout title="Appointments Management" {...props} />} 
          roles={['super_admin', 'admin_staff', 'admin']} 
          path="/admin/appointments"
        >
          <AppointmentsList />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/appointments/:id" element={
        <ProtectedRoute 
          component={(props) => <MobileLayout title="Appointment Details" {...props} />} 
          roles={['super_admin', 'admin_staff', 'admin']} 
          path="/admin/appointments/:id"
        >
          <AppointmentDetails />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/tickets" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/admin/tickets">
          <Suspense fallback={<LoadingFallback />}>
            <Tickets />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/admin/bookmyop-users" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/admin/bookmyop-users">
          <BookMyOpUsers />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/bookmyop/patients/:id" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/admin/bookmyop/patients/:id">
          <BookMyOpPatientDetails />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/tickets/:id" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/admin/tickets/:id">
          <Suspense fallback={<LoadingFallback />}>
            <TicketDetail />
          </Suspense>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/bda/new" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/admin/bda/new">
          <Suspense fallback={<LoadingFallback />}>
            <BDAForm />
          </Suspense>
        </ProtectedRoute>
      } />

      <Route path="/admin/bda/:id" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/admin/bda/:id">
          <Suspense fallback={<LoadingFallback />}>
            <BDADetail />
          </Suspense>
        </ProtectedRoute>
      } />

      <Route path="/admin/bda/:id/edit" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/admin/bda/:id/edit">
          <Suspense fallback={<LoadingFallback />}>
            <BDAForm />
          </Suspense>
        </ProtectedRoute>
      } />

      <Route path="/admin/walk-in" element={
        <ProtectedRoute 
          component={(props) => <MobileLayout title="Walk-in Appointments" {...props} />} 
          roles={['super_admin', 'admin_staff']} 
          path="/admin/walk-in"
        >
          <Suspense fallback={<LoadingFallback />}>
            <WalkInPatients />
          </Suspense>
        </ProtectedRoute>
      } />

      <Route path="/admin/walk-in/appointments/:id" element={
        <ProtectedRoute 
          component={(props) => <MobileLayout title="Walk-in Appointment Details" {...props} />} 
          roles={['super_admin', 'admin_staff']} 
          path="/admin/walk-in/appointments/:id"
        >
          <Suspense fallback={<LoadingFallback />}>
            <WalkInAppointmentDetails />
          </Suspense>
        </ProtectedRoute>
      } />

      <Route path="/dashboard" element={
        <ProtectedRoute 
          component={(props) => <MobileLayout title="Admin Dashboard" {...props} />} 
          roles={['super_admin', 'admin_staff', 'admin']} 
          path="/dashboard"
        >
          <Suspense fallback={<LoadingFallback />}>
            <Dashboard />
          </Suspense>
        </ProtectedRoute>
      } />

      <Route path="/admin/plugs/advertisements" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/admin/plugs/advertisements">
          <Suspense fallback={<LoadingFallback />}>
            <AdvertisementControl />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/admin/plugs/advertisement-costs" element={
        <ProtectedRoute component={MobileLayout} roles={['super_admin', 'admin_staff']} path="/admin/plugs/advertisement-costs">
          <Suspense fallback={<LoadingFallback />}>
            <AdvertisementCosts />
          </Suspense>
        </ProtectedRoute>
      } />

      
      <Route path="*" element={
        <Suspense fallback={<LoadingFallback />}>
          <NotFound />
        </Suspense>
      } />
    </Routes>
  );
};

const App = () => {
  return (
    <ThemeProvider>
      <SnackbarProvider 
        maxSnack={3}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <CssBaseline />
          <BrowserRouter>
            <AuthProvider>
              <DoctorViewProvider>
                <FilterProvider>
                  <NotificationProvider>
                    <ErrorBoundary>
                      <AppContent />
                    </ErrorBoundary>
                  </NotificationProvider>
                </FilterProvider>
              </DoctorViewProvider>
            </AuthProvider>
            {/* <ThemeSelector /> */}
          </BrowserRouter>
        </LocalizationProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
};

export default App;